/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {MouseEventHandler, useEffect, useRef, useState} from "react";
import {Icon, IconifyIcon} from '@iconify/react';
import {IconName, IconPrefix} from '@fortawesome/fontawesome-svg-core';
import {AwesomeIcon, stopPropagation} from '@dvrd/dvr-controls';
import rightArrow from '@iconify/icons-ant-design/arrow-right';
import circleCheckFill from '@iconify/icons-ant-design/check-circle-filled';
import classNames from "classnames";
import {AccessKey} from "../../util/interfaces";
import useRight from "../misc/useRight";

interface Props {
    onClick: MouseEventHandler
    onCheckTodo: (check: boolean) => void;
    icon: IconName | IconifyIcon;
    titleText: string;
    infoText: string;
    toggled: boolean;
    iconPrefix?: IconPrefix;
    keepClickable?: boolean;
    accessKey: AccessKey;
}

export default function Todo(props: Props) {
    const {onClick, onCheckTodo, icon, titleText, infoText, iconPrefix, toggled, keepClickable, accessKey} = props;
    const [hasRight] = useRight(accessKey);
    const [toggle, setToggle] = useState(toggled);
    const containerRef = useRef<HTMLDivElement>(null);

    function _onClick(evt: React.MouseEvent) {
        if ((keepClickable || !toggle) && hasRight)
            onClick(evt);
    }

    function onMouseEnter(evt: React.MouseEvent) {
        if (!keepClickable && toggle) return;
        toggleHoverClass(true)(evt);
    }

    function onMouseLeave(evt: React.MouseEvent) {
        toggleHoverClass(false)(evt);
    }

    function toggleCheck(isChecked: boolean) {
        return (evt: React.MouseEvent) => {
            evt.stopPropagation();
            setToggle(!isChecked)
            onCheckTodo(!isChecked);
        }
    }

    function toggleHoverClass(hovered: boolean, stopProp: boolean = false) {
        return (evt: React.MouseEvent) => {
            if (stopProp) stopPropagation(evt);
            const element = containerRef.current;
            if (!element) return;
            element.classList.toggle('hover', hovered);
        }
    }

    useEffect(() => {
        setToggle(props.toggled);
    }, [props.toggled]);

    return (
        <div className={classNames('todo', (!toggle || keepClickable) && 'enabled')} onClick={_onClick}
             onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} ref={containerRef}>
            {!toggle ? <div className={classNames('icon-container', 'link')}>
                {typeof icon === 'string' ?
                    <AwesomeIcon name={icon as IconName} prefix={iconPrefix}
                                 className='todo-icon'/> : <Icon icon={icon} className='todo-icon'/>}
            </div> : <div className={classNames('icon-container', 'check')}>
                <Icon icon={circleCheckFill} className='check-icon'/>
            </div>}
            <div className={classNames('text-container', toggle && 'disabled')}>
                <label className='todo-title'>{titleText}</label>
                <label className='todo-label'>{infoText}</label>
            </div>
            <div className={classNames('arrow-container', toggle && 'disabled')}>
                <Icon icon={rightArrow} className='arrow-icon'/>
            </div>
            {!toggle && <div className='check-container' onClick={toggleCheck(toggle)}
                             onMouseEnter={toggleHoverClass(false, true)}
                             onMouseLeave={!toggle ? toggleHoverClass(true, true) : undefined}>
                <AwesomeIcon name='circle-check' family="sharp" prefix="far"/>
            </div>}
        </div>
    )
}
